import { useState, useEffect } from 'react';
import { Button, Form } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';

import { DropdownSelectWithLabel, NumberInputWithLabel, DatePickerWithLabel } from '../../../components/common/standardComponents';
import ActiveErrors from '../../../components/common/activeErrors';
import LoadingIndicator from '../../../components/loadingIndicator';
import { validateDate, isInt32Valid } from '../../../lib/validation';

import * as appointmentActions from '../../../actions/appointmentActions';

import { useAppDispatch, useAppSelector } from '../../../store';

import '../css/searchAppointmentsFilters.css'

interface Services {
    id: number,
    name: string
}

function SearchAppointmentsFilters(props: any) {

    const {
        isDataEdited = false,
        setIsEditAllowed,
        handleShowConfirmationModal,
        results,
        setCriteria,
        setShowGrid,
        showGrid,
        onAppointmentSearch,
        setIsEditMode

    }: {
        isDataEdited: boolean,
        setIsEditAllowed: Function,
        handleShowConfirmationModal: Function,
        results: any,
        setCriteria: Function,
        setShowGrid: Function,
        showGrid: boolean,
        onAppointmentSearch: Function,
        setIsEditMode: Function
    } = props

    const auth = useAppSelector((state) => state.auth);
    const practiceSites = useAppSelector((state) => state.appointment.practiceSites);
    const maxSearchRange = useAppSelector((state) => state.config.appointmentManagement.appointmentSearchMaxDaysRange);

    const defaultOption = { id: 0, name: 'Select an option' }

    const [selectedPracticeSiteId, setSelectedPracticeSiteId] = useState<number>(0);
    const [selectedServiceId, setSelectedServiceId] = useState<number>(0);
    const [selectedStartDate, setSelectedStartDate] = useState<any>(moment().startOf('day'));
    const [selectedEndDate, setSelectedEndDate] = useState<any>(moment().endOf('day'));
    const [selectedBaseDate, setSelectedBaseDate] = useState<string>(moment(selectedStartDate).format('MM/DD/YYYY'));
    const [searchRange, setSearchRange] = useState<number>(1);
    const [isPracticeSitesLoaded, setIsPracticeSitesLoaded] = useState<boolean>(false);
    const [isServicesLoaded, setIsServicesLoaded] = useState<boolean>(true);
    const [serviceList, setServiceList] = useState<Services[]>([]);
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [isBaseDateInvalid, setIsBaseDateInvalid] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const onSiteChange = async (e: any) => {
        setSelectedPracticeSiteId(e.target.value);
        setSelectedServiceId(0);
        if (e.target.value !== "0") {
            setIsServicesLoaded(false);
            await dispatch(appointmentActions.getServicesForReferralSiteAtPracticeSite(e.target.value))
                .then((response: any) => {
                    if (!response.error) {
                        setServiceList([defaultOption, ...response.payload?.data]);
                    }
                    setIsServicesLoaded(true);
                })
        }
        else {
            setServiceList([defaultOption]);
            setSelectedServiceId(0);
        }
    }

    const onServiceChange = (e: any) => {
        setSelectedServiceId(e.target.value);
    }

    const onBaseDateChange = (e: any) => {
        if (validateDate(e.target.value)) {
            setSelectedBaseDate(e.target.value);
            setIsBaseDateInvalid(false);
        }
        else {
            setIsBaseDateInvalid(true);
        }
    }

    const onDatePickerChange = (date: any) => {
		setSelectedBaseDate(moment(date).format('MM/DD/YYYY'));
    }

    const onSearchRangeChange = (e: any) => {
        if (isInt32Valid(e.target.value, 1, maxSearchRange)) {
            setSearchRange(e.target.value);
        }
    }

    const handleSearch = () => {
        setIsEditAllowed(false);
        setIsSearching(true);
        setIsEditMode(false);
        const newCriteria = {
            practiceSiteId: selectedPracticeSiteId,
            serviceId: selectedServiceId,
            appointmentStartTime: moment.utc(selectedStartDate).format(),
            appointmentEndTime: moment.utc(selectedEndDate).format(),
            page: 1
        };
        if (showGrid) {
            onAppointmentSearch(newCriteria)
        }
        else {
            setCriteria((prevCriteria: any) => {
                const updatedCriteria = { ...prevCriteria, ...newCriteria };
                setShowGrid(true);
                return updatedCriteria;
            });
        }
    }

    const onSearch = async (e: any) => {
        e.preventDefault();
        if (isDataEdited) {
            handleShowConfirmationModal(handleSearch);
        }
        else {
            await handleSearch();
        }
    }

    useEffect(() => {
        if (isSearching) {
            setIsSearching(!(results?.data?.results));
            setIsEditAllowed(!moment(selectedBaseDate, 'MM/DD/YYYY').isAfter(moment()) && results?.data?.results);
        }
    }, [results])

    useEffect(() => {
        if (searchRange && selectedBaseDate) {
            setSelectedStartDate(moment(selectedBaseDate, 'MM/DD/YYYY').startOf('day'));
            setSelectedEndDate(moment(selectedBaseDate, 'MM/DD/YYYY').add(searchRange, 'days').endOf('day'));
        }
    }, [searchRange, selectedBaseDate])

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                await dispatch(appointmentActions.getPracticeSitesForReferralSite(auth.referralSiteId));
                setIsPracticeSitesLoaded(true);
            }
            catch (error: any) {
            }
        };
        fetchInitialData();
    }, []);

    return (
        <>
            <ActiveErrors />
            {isPracticeSitesLoaded ? (
                <div className='col-lg-12 siteReportFilterContent'>
                    <Form onSubmit={onSearch}>
                        <div className='row'>
                            <div className='col-3'>
                                <DropdownSelectWithLabel
                                    id='selectedSite'
                                    isRequired
                                    label='Practice Site'
                                    onChange={(e: any) => onSiteChange(e)}
                                    optionList={practiceSites}
                                    value={selectedPracticeSiteId} />
                            </div>
                            <div className='col-3'>
                                <DropdownSelectWithLabel
                                    id='selectedService'
                                    isRequired={false}
                                    label='Service'
                                    onChange={(e: any) => onServiceChange(e)}
                                    optionList={serviceList}
                                    value={selectedServiceId} />
                            </div>
                            <div className='col-2'>
                                <DatePickerWithLabel
                                    isRequired
                                    id='selectedDate'
                                    label='Date'
                                    value={selectedBaseDate}
                                    onChange={onBaseDateChange}
                                    onDatePickerChange={onDatePickerChange}
                                    forceInvalid={isBaseDateInvalid}
                                />
                            </div>
                            <div className='col-2'>
                                <NumberInputWithLabel
                                    isRequired
                                    id='serachRange'
                                    label='Search Range (days)'
                                    value={searchRange}
                                    onChange={onSearchRangeChange}
                                    min={1}
                                    max={maxSearchRange}
                                />
                            </div>
                            <div className='col-2 siteReportSearchButton'>
                                {!isServicesLoaded && <FontAwesomeIcon icon='spinner' spin />}
                                <Button color='primary' style={{ marginLeft: '5px' }} disabled={!selectedPracticeSiteId || selectedPracticeSiteId == 0 || !selectedBaseDate || isBaseDateInvalid || isSearching || !searchRange } type='submit'>
                                    {isSearching ? (
                                        <span>
                                            <FontAwesomeIcon icon='spinner' spin /> Searching...
                                        </span>
                                    ) : (
                                        <span>Search</span>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </div>
            ) : <LoadingIndicator />
            }
        </>
    )
}

export default SearchAppointmentsFilters;